/* eslint-disable arrow-body-style */
/* eslint-disable import/prefer-default-export */
const isFileValue = (value) => (typeof File !== 'undefined' && value instanceof File);
const isFileValueToStripOut = (value) =>
  (typeof File !== 'undefined' && value instanceof File)
  || (value && value.key && value.mimeType && value.size);

const stripFilesDeep = (value) => {
  if (isFileValueToStripOut(value)) return undefined;

  if (Array.isArray(value)) {
    const strippedArray = value
      .map((item) => stripFilesDeep(item))
      .filter((item) => item !== undefined);
    return strippedArray.length > 0 ? strippedArray : undefined;
  }

  if (value && typeof value === 'object') {
    const strippedObject = Object.entries(value).reduce((acc, [key, nestedValue]) => {
      const strippedNestedValue = stripFilesDeep(nestedValue);
      if (strippedNestedValue !== undefined) {
        acc[key] = strippedNestedValue;
      }
      return acc;
    }, {});

    return Object.keys(strippedObject).length > 0 ? strippedObject : undefined;
  }

  return value;
};

const appendPathSegment = (basePath, segment) => {
  if (typeof segment === 'number') {
    return `${basePath}.${segment}`;
  }
  return `${basePath}.${segment}`;
};

const collectFileUploadTasksDeep = (value, basePath, taskMeta, tasks) => {
  if (isFileValue(value)) {
    tasks.push({ ...taskMeta, fieldName: basePath, file: value });
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      collectFileUploadTasksDeep(item, appendPathSegment(basePath, index), taskMeta, tasks);
    });
    return;
  }

  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, nestedValue]) => {
      collectFileUploadTasksDeep(nestedValue, appendPathSegment(basePath, key), taskMeta, tasks);
    });
  }
};

export const collectCheckoutFileUploadTasks = (values) => {
  const tasks = [];

  Object.entries(values || {})
    .filter(([key, quantity]) => key.startsWith('product_') && Number(quantity) > 0)
    .forEach(([key]) => {
      const productId = parseInt(key.replace('product_', ''), 10);
      const attendees = values?.[`productAttendee_${productId}`] || [];
      attendees.forEach((attendee, attendeeIndex) => {
        const attribute = attendee?.attribute;
        if (!attribute) return;
        collectFileUploadTasksDeep(
          attribute,
          'attribute',
          {
            productId,
            attendeeId: attendee?.id ?? undefined,
            attendeeIndex: attendee?.id != null ? undefined : attendeeIndex,
          },
          tasks,
        );
      });
    });

  return tasks;
};

export const formatStateToCheckout = (values) => {
  const checkout = {
    invoiceAddress: values.invoiceAddress?.forename ? values.invoiceAddress : undefined,
    lineItems: Object.entries(values)
      .filter(([key]) => key.startsWith('product_'))
      .map(([key, value]) => ({
        productId: parseInt(key.replace('product_', '')),
        quantity: value,
        attendees: (values[`productAttendee_${key.replace('product_', '')}`] || [])
          .map((orgAttendee) => {
            const attendee = { ...orgAttendee };
            delete attendee.custom_attributes;
            return attendee;
          }),
      })),
  };

  return stripFilesDeep(checkout) || {};
};

export const formatCheckoutDataToState = (data) => {
  if (!data?.getDetails) return { invoiceAddress: { country: 'DE' } };
  return {
    invoiceAddress: data?.getCheckout?.invoiceAddress || { country: 'DE' },
    ...(data?.getDetails?.products?.reduce((acc, product) => {
      const lineItem = data?.getCheckout?.lineItems?.find(({ productId }) => productId === product.id);
      acc[`product_${product.id}`] = lineItem?.quantity || 0;
      acc[`productAttendee_${product.id}`] = lineItem?.attendees || [];
      return acc;
    }, {})),
  };
};

export const formatNumber = (value) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);

const normalizePriceDisplayMode = (mode) => {
  if (['configured', 'gross', 'net', 'both'].includes(mode)) return mode;
  return 'configured';
};

export const resolvePriceDisplayMode = (priceDisplayMode, priceModel) => {
  const normalizedMode = normalizePriceDisplayMode(priceDisplayMode);
  if (normalizedMode !== 'configured') return normalizedMode;
  return priceModel === 'net' ? 'net' : 'gross';
};

const hasSalePrice = (salePrice, basePrice) => (
  salePrice !== null
  && salePrice !== undefined
  && basePrice !== null
  && basePrice !== undefined
  && Number(salePrice) !== Number(basePrice)
);

export const getTicketCardPriceView = (product, effectiveMode) => {
  const gross = {
    price: product?.price,
    salePrice: product?.salesPrice,
    showSale: hasSalePrice(product?.salesPrice, product?.price),
  };
  const net = {
    price: product?.priceNet,
    salePrice: product?.salesPriceNet,
    showSale: hasSalePrice(product?.salesPriceNet, product?.priceNet),
  };

  return {
    mode: effectiveMode,
    gross,
    net,
  };
};
